import React, { useEffect, useState } from 'react';
import { CheckCircle, Download, Smartphone, X } from 'lucide-react';
import { usePwaInstall } from '../hooks/usePwaInstall';
import LscInstallInstructionsModal from './LscInstallInstructionsModal';
import LscInstallVisualSteps, {
  resolveInstallVisualPlatform,
} from './LscInstallVisualSteps';

interface LscInstallBannerProps {
  /** When true, only render on viewports below md. */
  mobileOnly?: boolean;
  /** Compact styling for account settings section. */
  variant?: 'banner' | 'section';
}

function InstallTroubleshootingNotes({
  platform,
  isAndroidInstallContext,
}: {
  platform: ReturnType<typeof resolveInstallVisualPlatform>;
  isAndroidInstallContext: boolean;
}) {
  if (platform === 'ios') {
    return null;
  }

  const showAndroidNotes = platform === 'android' || isAndroidInstallContext;

  return (
    <div
      className="rounded-lg border border-secondary-800/80 bg-secondary-950/40 px-3 py-2.5 text-xs text-secondary-500 space-y-1.5"
      data-testid={
        showAndroidNotes ? 'lsc-install-troubleshooting-android' : 'lsc-install-troubleshooting-generic'
      }
    >
      {showAndroidNotes ? (
        <>
          <p>
            If you don&apos;t see Install App, open this page in Chrome and interact with the page
            for a moment.
          </p>
          <p>
            Already installed? Remove the existing Stream Center app from your home screen before
            testing install again.
          </p>
          <p>Install prompts are controlled by Chrome and may appear after you browse or sign in.</p>
        </>
      ) : (
        <p>Install prompts are controlled by Chrome and may appear after you browse or sign in.</p>
      )}
    </div>
  );
}

const LscInstallBanner: React.FC<LscInstallBannerProps> = ({
  mobileOnly = false,
  variant = 'banner',
}) => {
  const {
    canPromptInstall,
    isStandalone,
    isIos,
    isAndroidInstallContext,
    promptInstall,
    dismissInstallBanner,
    resetInstallBannerDismiss,
    isInstallBannerDismissed,
  } = usePwaInstall();
  const [bannerDismissed, setBannerDismissed] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    setBannerDismissed(isInstallBannerDismissed());
  }, [isInstallBannerDismissed]);

  const showNativeInstall = canPromptInstall;
  const showIosHelp = isIos && !canPromptInstall;
  const installPlatform = resolveInstallVisualPlatform(isIos, canPromptInstall);
  const wrapperClass = mobileOnly ? 'md:hidden' : '';

  const handleDismiss = () => {
    dismissInstallBanner();
    setBannerDismissed(true);
  };

  const handleResetBannerReminder = () => {
    resetInstallBannerDismiss();
    setBannerDismissed(false);
  };

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const accepted = await promptInstall();
      if (accepted) {
        handleDismiss();
      }
    } finally {
      setInstalling(false);
    }
  };

  const openInstructions = () => setShowInstructions(true);

  const instructionsModal = (
    <LscInstallInstructionsModal
      open={showInstructions}
      onClose={() => setShowInstructions(false)}
      platform={installPlatform}
    />
  );

  if (isStandalone) {
    if (variant === 'section') {
      return (
        <div className={wrapperClass} data-testid="lsc-install-section-installed">
          <div className="card space-y-3">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-green-900/30 p-2 text-green-300 shrink-0">
                <CheckCircle className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0">
                <h2 className="font-semibold text-white">Installed on this device</h2>
                <p className="text-sm text-secondary-400 mt-1">
                  Stream Center is running as an installed app on this device.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  if (variant === 'section') {
    return (
      <div className={wrapperClass} data-testid="lsc-install-section">
        <div className="card space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary-900/40 p-2 text-primary-300 shrink-0">
              <Smartphone className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <h2 className="font-semibold text-white">Install Stream Center</h2>
              <p className="text-sm text-secondary-400">
                Add Stream Center to your phone. Open your league like an app.
              </p>
            </div>
          </div>

          <LscInstallVisualSteps platform={installPlatform} density="full" />

          <div className="flex flex-wrap gap-2">
            {showNativeInstall && (
              <button
                type="button"
                onClick={() => void handleInstall()}
                disabled={installing}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-60"
                data-testid="lsc-install-app-button"
              >
                <Download className="h-4 w-4" aria-hidden />
                Install App
              </button>
            )}
            <button
              type="button"
              onClick={openInstructions}
              className="inline-flex items-center gap-2 rounded-lg border border-secondary-600 px-4 py-2 text-sm font-medium text-secondary-200 hover:bg-secondary-800"
              data-testid="lsc-install-show-instructions"
            >
              Show install instructions
            </button>
            {bannerDismissed ? (
              <button
                type="button"
                onClick={handleResetBannerReminder}
                className="inline-flex items-center gap-2 rounded-lg border border-secondary-700 px-4 py-2 text-sm font-medium text-secondary-400 hover:bg-secondary-800 hover:text-secondary-200"
                data-testid="lsc-install-reset-banner-reminder"
              >
                Show banner again
              </button>
            ) : null}
          </div>

          <InstallTroubleshootingNotes
            platform={installPlatform}
            isAndroidInstallContext={isAndroidInstallContext}
          />
        </div>
        {instructionsModal}
      </div>
    );
  }

  if (bannerDismissed) {
    return null;
  }

  if (!showNativeInstall && !showIosHelp) {
    return null;
  }

  return (
    <>
      <div
        className={`rounded-lg border border-primary-700/60 bg-primary-900/25 p-4 ${wrapperClass}`}
        data-testid="lsc-install-banner"
        role="region"
        aria-label="Install Stream Center"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary-900/50 p-2 text-primary-300 shrink-0">
            <Smartphone className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm font-semibold text-white">Install Stream Center</p>
            <p className="text-xs text-secondary-300">
              Add Stream Center to your phone. Open your league like an app.
            </p>
            {(showNativeInstall || showIosHelp) && (
              <LscInstallVisualSteps
                platform={installPlatform}
                density="compact"
                className="pt-0.5"
              />
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {showNativeInstall && (
                <button
                  type="button"
                  onClick={() => void handleInstall()}
                  disabled={installing}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-500 disabled:opacity-60"
                  data-testid="lsc-install-app-button"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  Install App
                </button>
              )}
              {showIosHelp && (
                <button
                  type="button"
                  onClick={openInstructions}
                  className="inline-flex items-center gap-1.5 rounded-md border border-secondary-600 px-3 py-1.5 text-xs font-medium text-secondary-200 hover:bg-secondary-800"
                  data-testid="lsc-install-ios-button"
                >
                  How to Add to Home Screen
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 rounded p-1 text-secondary-500 hover:text-secondary-300"
            aria-label="Dismiss install banner"
            data-testid="lsc-install-banner-dismiss"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      {instructionsModal}
    </>
  );
};

export default LscInstallBanner;
